import { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Shield,
  Warning,
  Speed,
  BarChart,
} from '@mui/icons-material';
import { Gauge, gaugeClasses } from '@mui/x-charts';
import type { RiskMetrics, PositionRisk } from '../types';
import { fetchRiskMetrics, fetchPositionRisks } from '../services/api';

function RiskGauge({ value, max, label, unit = '%', color, warningThreshold }: {
  value: number; max: number; label: string; unit?: string; color: string; warningThreshold?: number;
}) {
  const pct = Math.min((value / max) * 100, 100);
  const isWarning = warningThreshold !== undefined && pct > warningThreshold * 100;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Gauge
        width={100}
        height={100}
        value={pct}
        valueMax={100}
        sx={{
          [`& .${gaugeClasses.valueArc}`]: { fill: isWarning ? '#f59e0b' : color },
          [`& .${gaugeClasses.referenceArc}`]: { fill: '#30363d' },
        }}
        text={({ value: displayValue }) => `${(displayValue ?? 0).toFixed(0)}${unit}`}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', color: isWarning ? 'warning.main' : 'text.secondary' }}>
        {value.toFixed(1)}{unit} / {max.toFixed(1)}{unit}
      </Typography>
    </Box>
  );
}

function MetricRow({ label, value, max, unit = '%', warningThresh = 0.8 }: {
  label: string; value: number; max: number; unit?: string; warningThresh?: number;
}) {
  const pct = Math.min((value / max) * 100, 100);
  const isWarning = pct > warningThresh * 100;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem', color: isWarning ? 'warning.main' : 'text.secondary' }}>
          {value.toFixed(2)}{unit}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: '#30363d',
          '& .MuiLinearProgress-bar': {
            backgroundColor: isWarning ? '#f59e0b' : '#6366f1',
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
}

interface Props { onNotify: (msg: string) => void; }

export default function RiskPanel({ onNotify }: Props) {
  const [metrics, setMetrics] = useState<RiskMetrics | null>(null);
  const [positionRisks, setPositionRisks] = useState<PositionRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [emergencyStop, setEmergencyStop] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, pr] = await Promise.all([
        fetchRiskMetrics().catch(() => null),
        fetchPositionRisks().catch(() => []),
      ]);
      setMetrics(m);
      setPositionRisks(pr);
      if (m) setEmergencyStop(m.emergencyStopActive);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !metrics) return <LinearProgress sx={{ mb: 2 }} />;

  const dailyLossLimit = metrics?.dailyLossLimit ?? 0.05;
  const currentDailyLoss = metrics?.currentDailyLoss ?? 0.018;
  const maxDD = metrics?.maxDrawdown ?? 0.15;
  const currentDD = metrics?.currentDrawdown ?? 0.04;
  const maxExposure = metrics?.maxExposure ?? 0.80;
  const totalExposure = metrics?.totalExposure ?? 0.42;
  const var95 = metrics?.var_95 ?? 0.025;
  const var99 = metrics?.var_99 ?? 0.04;
  const confidenceGate = metrics?.confidenceGate ?? 0.60;

  return (
    <>
      {/* ── Emergency Stop Banner ── */}
      {emergencyStop && (
        <Grid item xs={12}>
          <Alert severity="error" icon={<Shield fontSize="inherit" />}>
            EMERGENCY STOP ACTIVE — All trading is halted. Manually deactivate to resume.
          </Alert>
        </Grid>
      )}

      {/* ── Risk Gauges Row ── */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Daily Loss
                </Typography>
                <RiskGauge
                  value={currentDailyLoss * 100}
                  max={dailyLossLimit * 100}
                  label="vs Limit"
                  color="#ef4444"
                  warningThreshold={0.7}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Max Drawdown
                </Typography>
                <RiskGauge
                  value={currentDD * 100}
                  max={maxDD * 100}
                  label="vs Limit"
                  color="#f59e0b"
                  warningThreshold={0.7}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Portfolio Exposure
                </Typography>
                <RiskGauge
                  value={totalExposure * 100}
                  max={maxExposure * 100}
                  label="vs Max"
                  color="#6366f1"
                  warningThreshold={0.8}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Confidence Gate
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="h4" sx={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>
                    {(confidenceGate * 100).toFixed(0)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Min confidence to trade
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Risk Metrics Detail ── */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BarChart sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Risk Parameters
              </Typography>
            </Box>
            <MetricRow label="Daily Loss Limit" value={dailyLossLimit * 100} max={10} unit="%" warningThresh={0.5} />
            <MetricRow label="Current Daily Loss" value={currentDailyLoss * 100} max={10} unit="%" warningThresh={0.7} />
            <MetricRow label="Max Drawdown" value={maxDD * 100} max={20} unit="%" warningThresh={0.75} />
            <MetricRow label="Current Drawdown" value={currentDD * 100} max={20} unit="%" warningThresh={0.5} />
            <MetricRow label="Max Exposure" value={maxExposure * 100} max={100} unit="%" warningThresh={0.8} />
            <MetricRow label="Total Exposure" value={totalExposure * 100} max={100} unit="%" warningThresh={0.8} />
          </CardContent>
        </Card>
      </Grid>

      {/* ── VaR & Controls ── */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Speed sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Value at Risk &amp; Controls
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">VaR 95% (1-day)</Typography>
                  <Typography variant="caption" color="text.secondary">Monte Carlo simulation</Typography>
                </Box>
                <Chip label={`-${(var95 * 100).toFixed(2)}%`} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace" }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">VaR 99% (1-day)</Typography>
                  <Typography variant="caption" color="text.secondary">Historical simulation</Typography>
                </Box>
                <Chip label={`-${(var99 * 100).toFixed(2)}%`} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace" }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">Correlation Risk</Typography>
                  <Typography variant="caption" color="text.secondary">Cross-position correlation</Typography>
                </Box>
                <Chip label={metrics?.correlationRisk?.toFixed(2) ?? '0.00'} size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">Emergency Stop</Typography>
                  <Typography variant="caption" color="text.secondary">Halt all trading</Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emergencyStop}
                      onChange={(e) => {
                        setEmergencyStop(e.target.checked);
                        onNotify(e.target.checked ? 'Emergency stop activated' : 'Emergency stop deactivated');
                      }}
                      color="error"
                    />
                  }
                  label=""
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* ── Per-Position Risk Table ── */}
      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Position-Level Risk ({positionRisks.length} positions)
                </Typography>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Symbol</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right">Margin</TableCell>
                    <TableCell align="right">Leverage</TableCell>
                    <TableCell align="right">Risk Score</TableCell>
                    <TableCell align="right">Liq. Distance</TableCell>
                    <TableCell align="right">Exposure</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positionRisks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="caption" color="text.secondary" sx={{ py: 2, display: 'block' }}>
                          No open positions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {positionRisks.map((pr) => (
                    <TableRow key={pr.positionId}>
                      <TableCell>
                        <Chip label={pr.symbol} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                        {pr.size.toFixed(4)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                        ${pr.margin.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={`${pr.leverage}×`} size="small" sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#30363d' }} />
                      </TableCell>
                      <TableCell align="right">
                        <LinearProgress
                          variant="determinate"
                          value={pr.riskScore * 100}
                          sx={{
                            width: 60, display: 'inline-flex', height: 6, borderRadius: 3, mr: 1,
                            '& .MuiLinearProgress-bar': { backgroundColor: pr.riskScore > 0.7 ? '#ef4444' : pr.riskScore > 0.4 ? '#f59e0b' : '#10b981' },
                          }}
                        />
                        <Typography component="span" variant="caption" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          {(pr.riskScore * 100).toFixed(0)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', color: pr.liquidationDistance < 10 ? 'error.main' : 'text.secondary' }}>
                        {pr.liquidationDistance.toFixed(1)}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem' }}>
                        {(pr.exposure * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
}
